import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { gbp, formatDate } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Scale } from "lucide-react";

export default function Reconciliation() {
  const [fy, setFy] = useState("FY27");
  const [site, setSite] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["reconciliation", fy, site], queryFn: () => api.get("/reconciliation", { params: { fy, site: site === "all" ? "" : site } }).then((r) => r.data) });

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const fyOptions = data?.fy_options || ["FY27"];
  const siteOptions = data?.site_options || [];
  const [detailPeriod, setDetailPeriod] = useState(null);
  const { data: detail } = useQuery({
    queryKey: ["recon-detail", detailPeriod, site],
    queryFn: () => api.get("/reconciliation/detail", { params: { period: detailPeriod, site: site === "all" ? "" : site } }).then((r) => r.data),
    enabled: !!detailPeriod,
  });

  const exportCsv = () => {
    const headers = ["period_label", "month_label", "weeks", "days", "start", "end", "cogs", "savings", "service_credits", "net_due"];
    downloadCsv(`epgroup-reconciliation-${fy}.csv`, headers, rows.map((r) => ({ ...r, start: formatDate(r.start), end: formatDate(r.end) })));
  };

  return (
    <div>
      <PageHeader title="Year 1 Reconciliation" subtitle={site === "all" ? "All sites — ties calendar periods to cost of goods, savings and service credits." : `${site} — cost of goods and savings for the selected site, with company-wide service credits applied.`}>
        <Select value={site} onValueChange={setSite}>
          <SelectTrigger className="w-full sm:w-44" data-testid="recon-site-select"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All sites</SelectItem>{siteOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-full sm:w-36" data-testid="recon-fy-select"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} data-testid="recon-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
      </PageHeader>

      {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState message="No calendar periods for this financial year." /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Cost of Goods", value: gbp(totals.cogs), tint: "text-amber-600" },
              { label: "Total Savings", value: gbp(totals.savings), tint: "text-emerald-600" },
              { label: "Service Credits", value: gbp(totals.service_credits), tint: "text-rose-600" },
              { label: "Net Due", value: gbp(totals.net_due), tint: "text-blue-700" },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <p className={`text-2xl font-display font-bold font-mono-num ${m.tint}`}>{m.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>

          <TableShell>
            <table className="w-full text-sm" data-testid="reconciliation-table">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Month</th>
                  <th className="px-4 py-3 text-center">Weeks</th>
                  <th className="px-4 py-3 text-center">Days</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3 text-right">Cost of Goods</th>
                  <th className="px-4 py-3 text-right">Savings</th>
                  <th className="px-4 py-3 text-right">Service Credits</th>
                  <th className="px-4 py-3 text-right">Net Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.period_label} onClick={() => setDetailPeriod(r.period_label)} className="hover:bg-blue-50/60 dark:hover:bg-slate-800/40 cursor-pointer" data-testid={`recon-row-${r.period_label}`} title="Click to see underlying invoices & savings">
                    <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">{r.period_label}</td>
                    <td className="px-4 py-2.5 font-mono-num text-slate-500">{r.month_label}</td>
                    <td className="px-4 py-2.5 text-center font-mono-num">{r.weeks}</td>
                    <td className="px-4 py-2.5 text-center font-mono-num">{r.days}</td>
                    <td className="px-4 py-2.5 font-mono-num text-slate-600">{formatDate(r.start)}</td>
                    <td className="px-4 py-2.5 font-mono-num text-slate-600">{formatDate(r.end)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{gbp(r.cogs)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-600">{gbp(r.savings)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-rose-600">{gbp(r.service_credits)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{gbp(r.net_due)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-800/60 font-semibold">
                  <td className="px-4 py-3" colSpan={6}>Totals ({totals.weeks} weeks)</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(totals.cogs)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{gbp(totals.savings)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-rose-700">{gbp(totals.service_credits)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-blue-700">{gbp(totals.net_due)}</td>
                </tr>
              </tfoot>
            </table>
          </TableShell>
        </>
      )}

      <Dialog open={!!detailPeriod} onOpenChange={(v) => !v && setDetailPeriod(null)}>
        <DialogContent className="max-w-2xl" data-testid="recon-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailPeriod} — underlying detail</DialogTitle>
            <DialogDescription>{site === "all" ? "All sites" : site} · invoices and savings lines that make up this period.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Cost of Goods</p>
              {detail?.cogs?.length ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs text-slate-500"><th className="px-3 py-2">Site</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">COGS</th></tr></thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {detail.cogs.map((c) => <tr key={c.id}><td className="px-3 py-2 text-slate-600">{c.site || "—"}</td><td className="px-3 py-2">{c.product_description || "—"}</td><td className="px-3 py-2 text-right font-mono-num">{gbp(c.cogs)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-slate-400">No cost of goods lines for this period.</p>}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Savings</p>
              {detail?.savings?.length ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs text-slate-500"><th className="px-3 py-2">Site</th><th className="px-3 py-2 text-right">Savings</th><th className="px-3 py-2 text-right">Closing</th></tr></thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {detail.savings.map((s) => <tr key={s.id}><td className="px-3 py-2 text-slate-600">{s.site || "—"}</td><td className="px-3 py-2 text-right font-mono-num text-emerald-600">{gbp(s.savings_in_period)}</td><td className="px-3 py-2 text-right font-mono-num">{gbp(s.closing_balance)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-slate-400">No savings lines for this period.</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
